import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Rocket,      
  CalendarDays, 
  ShieldCheck, 
  UsersRound,  
  Settings2,   
  CheckCircle, 
  ChevronRight // Added for RTL consistency
} from 'lucide-react';
import { Button } from '@/components/ui/button';
// Card and CardContent are not used, can be removed if not planned for future use
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// Animation variants (consistent with other sections)
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const staggerChildren = {
  hidden: { opacity: 0 },
  // Adjusted staggerChildren delay for potentially more items
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } } 
};

export default function TrialCtaSection({ viewport, isRTL }) { 
  const { t } = useTranslation();
  const { isMobile = false } = viewport || {};

  // Using HSL theme colors for stats and highlights for better maintainability
  const stats = [
    {
      key: 'days',
      value: '24',
      icon: CalendarDays,
      color: 'hsl(var(--sky-500))' // Example: using sky blue from theme
    },
    {
      key: 'uptime',
      value: '99.9%',
      icon: ShieldCheck,
      color: 'hsl(var(--green-500))' // Example: using green from theme
    },
    {
      key: 'support',
      value: '24/7',
      icon: UsersRound,
      color: 'hsl(var(--amber-500))' // Example: using amber from theme
    },
    {
      key: 'setup',
      value: '$0',
      icon: Settings2,
      color: 'hsl(var(--violet-500))' // Example: using violet from theme
    }
  ].map(stat => ({ ...stat, label: t(`homepage.trial.stats.${stat.key}`) }));

  const highlights = [
    { key: 'instant', icon: Rocket, color: 'hsl(var(--blue-500))' }, // Added color
    { key: 'fullSupport', icon: UsersRound, color: 'hsl(var(--amber-500))' }, // Added color
    { key: 'noLimits', icon: CheckCircle, color: 'hsl(var(--green-500))' } // Added color
  ].map(hl => ({ 
    ...hl, 
    title: t(`homepage.trial.features.${hl.key}.title`), 
    description: t(`homepage.trial.features.${hl.key}.description`)
  }));

  return (
    <section className="relative py-20 md:py-28 lg:py-32 bg-gradient-to-br from-primary to-[hsl(var(--primary)_/_0.9)] text-primary-foreground overflow-hidden">
      {/* Refined background visuals for subtlety */}
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="smallGridCta" width="24" height="24" patternUnits="userSpaceOnUse"><path d="M 24 0 L 0 0 0 24" fill="none" stroke="currentColor" strokeWidth="0.4"/></pattern></defs><rect width="100%" height="100%" fill="url(#smallGridCta)" /></svg>
      </div>
      <motion.div 
        className="absolute -top-1/4 -right-1/4 w-1/2 h-1/2 bg-primary-foreground/5 rounded-full filter blur-3xl opacity-30 animate-pulse-slow"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.3 }}
        transition={{ duration: 2, delay: 0.5, ease: "easeInOut"}}
      />
      <motion.div 
        className="absolute -bottom-1/4 -left-1/4 w-1/3 h-1/3 bg-primary-foreground/5 rounded-full filter blur-3xl opacity-20 animate-pulse-slow-reverse"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.2 }}
        transition={{ duration: 2, delay: 0.8, ease: "easeInOut"}}
      />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }} // Standard viewport amount
          variants={staggerChildren}
          className="max-w-3xl mx-auto text-center"
        >
          <motion.div variants={fadeInUp}>
            {/* Standardized badge styling */}
            <Badge variant="outline" className="mb-4 px-4 py-1.5 text-sm font-medium rounded-full bg-primary-foreground/10 text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/20 transition-colors">
              {t('homepage.trial.badge', 'Risk-Free Exploration')}
            </Badge>
          </motion.div>
          <motion.div variants={fadeInUp}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 tracking-tight">
              {t('homepage.trial.title', 'Ready to Elevate Your Operations?')}
            </h2>
          </motion.div>
          <motion.div variants={fadeInUp}>
            <p className="text-lg md:text-xl text-primary-foreground/80 max-w-2xl mx-auto mb-8 md:mb-10 leading-relaxed">
              {t('homepage.trial.subtitle', 'Unlock the full potential of your fleet and IoT assets. Start your 24-hour free trial today—no credit card required, no hidden fees.')}
            </p>
          </motion.div>
          
          {/* Standardized Buttons */}
          <motion.div 
            variants={fadeInUp} 
            className={cn(
              "flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-12 md:mb-16", // Adjusted gap
              isRTL && "sm:flex-row-reverse"
            )}
          >
            <Button 
              size={isMobile ? "lg" : "lg"} // Consistent lg size for main CTAs
              className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-black font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 group"
            >
              <Rocket size={18} className={cn("transition-transform group-hover:rotate-[12deg]", isRTL ? "ml-2" : "mr-2")} />
              {t('homepage.trial.startButton', 'Start Free Trial')}
            </Button>
            <Button 
              variant="outline"
              size={isMobile ? "lg" : "lg"}
              className="w-full sm:w-auto border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10 hover:border-primary-foreground/70 font-medium shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 group"
            >
              <CalendarDays size={18} className={cn("transition-transform group-hover:scale-105", isRTL ? "ml-2" : "mr-2")} />
              {t('homepage.trial.scheduleButton', 'Schedule a Demo')}
            </Button>
          </motion.div>

          {/* Statistics Grid - Improved styling and responsiveness */}
          <motion.div 
            variants={staggerChildren} // Stagger children for this grid too
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: isMobile ? 0.1 : 0.2 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5 mb-12 md:mb-16"
          >
            {stats.map((stat) => {
              const StatIcon = stat.icon;
              return (
                <motion.div 
                  key={stat.key} 
                  variants={fadeInUp} // Animate each stat item
                  className="text-center p-3 sm:p-4 bg-primary-foreground/[.07] rounded-lg border border-primary-foreground/10 hover:bg-primary-foreground/[.12] transition-colors duration-200"
                >
                  <StatIcon size={isMobile ? 24 : 28} className={cn("mb-1.5 sm:mb-2 mx-auto")} style={{ color: stat.color }} />
                  <div className={`text-xl sm:text-2xl font-bold mb-0.5`} style={{ color: stat.color }}>
                    {stat.value}
                  </div>
                  <div className="text-xs sm:text-sm text-primary-foreground/70">
                    {stat.label}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Features highlight - Improved styling and layout */}
          <motion.div 
            variants={staggerChildren} // Stagger children for highlights
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: isMobile ? 0.1 : 0.2 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 text-center md:text-left"
          >
            {highlights.map((highlight) => {
              const HighlightIcon = highlight.icon;
              return (
                <motion.div 
                  key={highlight.key} 
                  variants={fadeInUp} // Animate each highlight item
                  className="p-4 sm:p-5 bg-primary-foreground/[.07] rounded-lg border border-primary-foreground/10 hover:bg-primary-foreground/[.12] transition-colors duration-200 flex flex-col items-center md:items-start"
                >
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center mb-3 transition-all duration-300 group-hover:scale-110"
                    style={{ 
                      backgroundColor: `color-mix(in srgb, ${highlight.color} 20%, transparent)`,
                      color: highlight.color
                    }}
                  >
                    <HighlightIcon size={20} />
                  </div>
                  <h3 className="text-base sm:text-md font-semibold text-primary-foreground mb-1.5">
                    {highlight.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-primary-foreground/70 leading-relaxed">
                    {highlight.description}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
          
        </motion.div>
      </div>
    </section>
  );
} 